/**
 * Logger Service - Centralized logging
 * Provides consistent logging across the application
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string;
  data?: unknown;
  timestamp: Date;
}

export class LoggerService {
  private static isDevelopment = process.env.NODE_ENV === 'development';
  private static isProduction = process.env.NODE_ENV === 'production';

  /**
   * Log debug information (only in development)
   */
  static debug(message: string, context?: string, data?: unknown): void {
    if (this.isDevelopment) {
      this.log(LogLevel.DEBUG, message, context, data);
    }
  }

  /**
   * Log info messages
   */
  static info(message: string, context?: string, data?: unknown): void {
    this.log(LogLevel.INFO, message, context, data);
  }

  /**
   * Log warnings
   */
  static warn(message: string, context?: string, data?: unknown): void {
    this.log(LogLevel.WARN, message, context, data);
  }

  /**
   * Log errors
   */
  static error(message: string, context?: string, data?: unknown): void {
    this.log(LogLevel.ERROR, message, context, data);
  }

  /**
   * Log user actions for analytics
   */
  static logUserAction(action: string, context?: string, data?: unknown): void {
    if (this.isProduction) {
      // In production, send to analytics service
      this.log(LogLevel.INFO, `User Action: ${action}`, context, data);
    } else {
      this.debug(`User Action: ${action}`, context, data);
    }
  }

  /**
   * Log performance metrics
   */
  static logPerformance(operation: string, duration: number, context?: string): void {
    this.info(`Performance: ${operation} took ${duration}ms`, context);
  }

  /**
   * Internal logging method
   */
  private static log(level: LogLevel, message: string, context?: string, data?: unknown): void {
    const entry: LogEntry = {
      level,
      message,
      context,
      data,
      timestamp: new Date()
    };

    const logMessage = `[${entry.timestamp.toISOString()}] [${level.toUpperCase()}] ${context ? `[${context}]` : ''} ${message}`;

    switch (level) {
      case LogLevel.DEBUG:
        console.debug(logMessage, data);
        break;
      case LogLevel.INFO:
        console.info(logMessage, data);
        break;
      case LogLevel.WARN:
        console.warn(logMessage, data);
        break;
      case LogLevel.ERROR:
        console.error(logMessage, data);
        break;
    }
  }
}
